import { useEffect, useState } from "react";
import { Bot, Languages, Loader2, MessageSquareText, UserRound } from "lucide-react";
import { cn } from "../../lib/utils";
import type { CallRecord, TranscriptLine } from "./types";
import { useTranscriptTranslation } from "./use-transcript-translation";

interface TranscriptGroup {
  speaker: TranscriptLine["speaker"];
  at: string;
  lines: { text: string; index: number }[];
  /** True when at least one turn in the group was cut off by the caller. */
  cut: boolean;
}

/**
 * What the caller actually heard from a turn.
 *
 * A recorded turn's `text` is what the model generated, not what was spoken: the
 * caller barged in, the audio was flushed, and the tail was never voiced. The
 * worker records the measured spoken prefix, so showing that instead is the
 * difference between a transcript and a guess.
 *
 * Only the *measured* prefix is honoured. Falling back to the full text when it
 * is missing keeps calls recorded before this existed rendering normally, and
 * never invents a truncation from a ratio or a timestamp.
 */
function heardText(line: TranscriptLine): string {
  if (typeof line.spokenPrefix !== "string") return line.text;
  return line.spokenPrefix;
}

function groupTranscript(lines: TranscriptLine[]): TranscriptGroup[] {
  const groups: TranscriptGroup[] = [];
  lines.forEach((line, index) => {
    const heard = heardText(line).trim();
    // A turn cut before any audio leaves nothing to show. Keep it in the
    // transcript -- dropping the line entirely would hide the turn -- but do not
    // invent text for it, and mark the group so it renders as a cut.
    const cut = typeof line.spokenPrefix === "string" && line.spokenPrefix !== line.text;
    if (!heard && !cut) return;

    const text = heard || `(${line.speaker === "agent" ? "Agent" : "Contact"} said nothing)`;

    const previous = groups[groups.length - 1];
    if (previous?.speaker === line.speaker) {
      previous.lines.push({ text, index });
      previous.cut = previous.cut || cut;
      return;
    }

    groups.push({
      speaker: line.speaker,
      at: line.at,
      lines: [{ text, index }],
      cut,
    });
  });
  return groups;
}

export function TranscriptConversation({
  call,
  className,
}: {
  call: CallRecord;
  className?: string;
}) {
  const groups = groupTranscript(call.transcript);
  const { translated, translating, error, translate, reset } = useTranscriptTranslation();
  const [showTranslated, setShowTranslated] = useState(false);

  // New call -> drop cached translation so lines never leak across calls.
  useEffect(() => {
    reset();
    setShowTranslated(false);
  }, [call.executionId, reset]);

  // Flat original texts in render order; translated[] stays index-aligned.
  const flatOriginals = call.transcript.map((line) => line.text);

  const handleSeeTranslation = async () => {
    if (translated) {
      setShowTranslated(true);
      return;
    }
    const result = await translate(flatOriginals, {
      callId: call.callId || call.executionId,
      contactId: call.contactId,
    });
    if (result) setShowTranslated(true);
  };

  const handleSeeOriginal = () => setShowTranslated(false);

  /** Text to render for the n-th transcript line, original or English. */
  const textForIndex = (index: number, original: string) =>
    showTranslated && translated ? translated[index] ?? original : original;

  const hasTranscript = call.transcript.length > 0;

  return (
    <div className={cn("overflow-hidden rounded-lg border border-line bg-canvas", className)}>
      <div className="flex items-center justify-between gap-2 border-b border-line bg-elevated px-4 py-3">
        <div className="flex items-center gap-2">
          <MessageSquareText className="h-4 w-4 text-ink-3" />
          <span className="text-sm font-semibold text-ink">Conversation</span>
        </div>
        <div className="flex items-center gap-2">
          {hasTranscript && (
            <button
              type="button"
              onClick={showTranslated ? handleSeeOriginal : handleSeeTranslation}
              disabled={translating}
              title={showTranslated ? "Show the original transcript" : "Translate the full transcript to English"}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-semibold transition",
                showTranslated
                  ? "border-line bg-canvas text-ink-2 hover:text-ink"
                  : "border-brand/30 bg-brand-soft text-brand hover:bg-brand-soft/70",
                translating && "opacity-70",
              )}
            >
              {translating ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Languages className="h-3 w-3" />
              )}
              {translating ? "Translating…" : showTranslated ? "See original" : "See translation"}
            </button>
          )}
          <span className="rounded bg-soft px-2 py-1 font-mono text-[10px] font-semibold text-ink-3">
            {call.transcript.length} message{call.transcript.length === 1 ? "" : "s"}
          </span>
        </div>
      </div>
      {!showTranslated && error && (
        <p className="border-b border-line bg-soft px-4 py-1.5 text-[11px] text-danger">{error}</p>
      )}

      {groups.length > 0 ? (
        <div className="space-y-5 p-4">
          {groups.map((group, index) => {
            const isAgent = group.speaker === "agent";
            const speakerName = isAgent ? call.agent || "Agent" : "Contact";
            const Icon = isAgent ? Bot : UserRound;

            return (
              <div
                key={`${group.speaker}-${index}`}
                className={cn("flex items-end gap-2.5", !isAgent && "flex-row-reverse")}
              >
                <span
                  className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border",
                    isAgent
                      ? "border-orange-200 bg-orange-50 text-orange-600"
                      : "border-line bg-elevated text-ink-2",
                  )}
                >
                  <Icon className="h-4 w-4" />
                </span>

                <div className={cn("min-w-0 max-w-[84%]", !isAgent && "text-right")}>
                  <div
                    className={cn(
                      "mb-1 flex items-center gap-2 px-1",
                      !isAgent && "flex-row-reverse",
                    )}
                  >
                    <span
                      className={cn(
                        "truncate text-xs font-semibold",
                        isAgent ? "text-orange-700" : "text-ink-2",
                      )}
                    >
                      {speakerName}
                    </span>
                    {group.at && (
                      <span className="shrink-0 font-mono text-[10px] text-ink-3">{group.at}</span>
                    )}
                  </div>

                  <div
                    className={cn(
                      "space-y-2 rounded-lg border px-3.5 py-3 text-left text-[14px] leading-6 shadow-sm",
                      isAgent
                        ? "border-orange-100 bg-elevated text-ink"
                        : "border-line bg-soft text-ink",
                      // A cut turn reads as unfinished, so tint the bubble the
                      // same way the live transcript does. Border only -- no new
                      // element, no label, the sentence is just cut short.
                      group.cut && isAgent && "border-dashed border-orange-300",
                    )}
                  >
                    {group.lines.map(({ text, index: lineFlatIndex }, lineIndex) => {
                      // A cut turn is the last line of its group, since nothing
                      // followed it. Only that one is marked -- an earlier line in
                      // the same run was spoken through.
                      const marked = group.cut && isAgent && lineIndex === group.lines.length - 1;
                      return (
                        <p
                          key={lineFlatIndex}
                          data-testid={marked ? "recorded-cut-turn" : undefined}
                          className={cn(
                            "whitespace-pre-wrap break-words",
                            marked && "italic",
                          )}
                          title={marked ? "The caller cut the agent off here" : undefined}
                        >
                          {textForIndex(lineFlatIndex, text)}
                        </p>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="px-4 py-10 text-center">
          <MessageSquareText className="mx-auto h-7 w-7 text-slate-300" />
          <p className="mt-2 text-sm text-ink-3">No transcript is available for this call.</p>
        </div>
      )}
    </div>
  );
}
